import {
  createInternalCategory,
  getErrorMessage,
  updateInternalCategory,
  useInternalCategories,
  useInternalCategory,
  useProducts,
} from '@gozoki/api'
import {
  useInternalCategoryForm,
  type InternalCategoryForm,
} from '@gozoki/api/src/forms/useInternalCategoryForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { XIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useController } from 'react-hook-form'
import { toast } from 'sonner'
import { Field } from '~/components/new/app-ui/form-fields/field'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Switch } from '~/components/ui/switch'
import { useRights } from '../../../utils/hooks'
import { ProductDualListPanel } from '../../inputs/ProductDualListPanel'

const DUPLICATE_INTERNAL_CATEGORY_NAME_MESSAGE =
  'Veuillez choisir un nouveau nom, une catégorie interne porte déjà ce nom.'

const normalizeCategoryName = (value: string) => value.trim().toLocaleLowerCase('fr')

interface InternalCategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  internalCategoryId?: number
  onSuccess?: () => void
}

export const InternalCategoryDialog = ({
  open,
  onOpenChange,
  internalCategoryId,
  onSuccess,
}: InternalCategoryDialogProps) => {
  const isUserAdmin = useRights(['ADMIN'])
  const queryClient = useQueryClient()
  const updateMode = internalCategoryId !== undefined

  const { data } = useInternalCategory(internalCategoryId)
  const { data: products } = useProducts()
  const { data: internalCategories } = useInternalCategories()

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useInternalCategoryForm()

  const { field: nameField } = useController({ control, name: 'name' })
  const { field: trackDlcField } = useController({ control, name: 'trackDlc' })
  const { field: productIdsField } = useController({ control, name: 'productIds' })

  const selectedProductIds = Array.isArray(productIdsField.value) ? productIdsField.value : []
  const activeProducts = useMemo(
    () => (products ?? []).filter((product) => product.isActive),
    [products],
  )

  const internalCategoryLabelById = useMemo(
    () =>
      (internalCategories ?? []).reduce(
        (acc, category) => {
          acc[category.id] = category.name
          return acc
        },
        {} as Record<number, string>,
      ),
    [internalCategories],
  )

  const productItems = useMemo(
    () =>
      activeProducts.map((product) => {
        const groupId = product.internalCategoryId ?? 0
        return {
          id: product.id,
          label: product.label,
          groupId,
          groupLabel: internalCategoryLabelById[groupId] ?? 'AUTRE',
        }
      }),
    [activeProducts, internalCategoryLabelById],
  )

  useEffect(() => {
    if (!open || updateMode) return
    reset()
  }, [open, updateMode, reset])

  useEffect(() => {
    if (!data) return
    setValue('name', data.name)
    setValue('trackDlc', data.trackDlc)
    setValue('productIds', data.products)
  }, [data, setValue])

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] })
    queryClient.invalidateQueries({ queryKey: ['internalCategories'] })
    onSuccess?.()
    onOpenChange(false)
  }

  const setDuplicateNameError = () => {
    setError('name', {
      type: 'manual',
      message: DUPLICATE_INTERNAL_CATEGORY_NAME_MESSAGE,
    })
  }

  const isDuplicateNameError = (error: unknown) => {
    const errorData =
      error instanceof AxiosError && error.response?.data && typeof error.response.data === 'object'
        ? (error.response.data as Record<string, unknown>)
        : null

    const candidates = [errorData?.code, errorData?.message, errorData?.error]
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.toLowerCase())

    return candidates.some(
      (value) =>
        value.includes('23505') ||
        value.includes('duplicate key') ||
        value.includes('internal_categories_name_unique'),
    )
  }

  const hasDuplicateInternalCategoryName = (name: string) => {
    const normalizedName = normalizeCategoryName(name)
    if (!normalizedName) {
      return false
    }

    return (internalCategories ?? []).some((category) => {
      if (updateMode && category.id === internalCategoryId) {
        return false
      }

      return normalizeCategoryName(category.name) === normalizedName
    })
  }

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: createInternalCategory,
    onSuccess: handleSuccess,
    onError: (error: unknown) => {
      if (!updateMode && isDuplicateNameError(error)) {
        setDuplicateNameError()
        return
      }
      toast.error(getErrorMessage(error))
    },
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: (formData: InternalCategoryForm) =>
      updateInternalCategory(internalCategoryId ?? 0, formData),
    onSuccess: handleSuccess,
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (formData: InternalCategoryForm) => {
    if (!updateMode && hasDuplicateInternalCategoryName(formData.name)) {
      setDuplicateNameError()
      return
    }

    clearErrors('name')

    if (updateMode) {
      update(formData)
    } else {
      create(formData)
    }
  }

  const isPending = isCreating || isUpdating

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[800px] max-w-[calc(100%-2rem)] flex flex-col p-0 gap-0"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9eaeb]">
          <DialogTitle className="text-[18px] text-[#181d27] font-normal">
            {updateMode
              ? isUserAdmin
                ? 'Modifier la catégorie'
                : 'Voir la catégorie'
              : 'Ajouter une catégorie interne'}
          </DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" />}>
            <XIcon />
            <span className="sr-only">Fermer</span>
          </DialogClose>
        </div>

        {/* Body */}
        <form
          id="internal-category-form"
          onSubmit={handleSubmit(onSubmit, () => {
            toast.error('Vérifiez les champs du formulaire')
          })}
          className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-5"
        >
          {/* Name field */}
          <Field label="Nom de la catégorie" error={errors.name?.message}>
            <Input
              value={nameField.value ?? ''}
              onChange={(event) => {
                nameField.onChange(event)
                if (errors.name?.type === 'manual') {
                  clearErrors('name')
                }
              }}
              onBlur={() => trigger('name')}
              disabled={!isUserAdmin}
            />
          </Field>

          <div className="flex items-center gap-3">
            <Switch
              id="track-dlc"
              checked={trackDlcField.value === true}
              onCheckedChange={(checked) => trackDlcField.onChange(checked)}
              disabled={!isUserAdmin}
            />
            <Label htmlFor="track-dlc" className="cursor-pointer">
              Inclure cette catégorie dans le suivi DLC hebdomadaire
            </Label>
          </div>

          <ProductDualListPanel
            productItems={productItems}
            categoryId={internalCategoryId}
            categoryName={nameField.value || undefined}
            value={selectedProductIds}
            onChange={productIdsField.onChange}
            disabled={!isUserAdmin}
          />
        </form>

        {/* Footer */}
        <DialogFooter className="shrink-0 px-6 py-4 border-t border-[#e9eaeb]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-[14px] text-[#374151] border border-[#e9eaeb] rounded-full hover:bg-gray-50"
          >
            Annuler
          </button>
          {isUserAdmin && (
            <button
              type="submit"
              form="internal-category-form"
              disabled={isPending}
              className="px-4 py-2 text-[14px] text-white bg-[#6a9560] rounded-full hover:bg-[#5a8350] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enregistrer
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
